import { useNavigationPresentation, tradeActionVisible, tradeActionLabel } from "@/lib/tradeNavigation";
// "+ Create" — grouped global create menu in the top bar.
// Deep-links use `?new=1` where the destination page auto-opens its create
// dialog; quote/invoice workspaces have dedicated /new routes.
import { useNavigate } from "react-router-dom";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Plus, ChevronDown, Clock, Hammer, FileText, ShieldAlert, UserPlus, ClipboardCheck,
  ClipboardList, Calendar, ShoppingCart, PencilRuler, Receipt, FileSignature,
  CalendarDays, Wrench, ArrowRight,
} from "lucide-react";
import { useState } from "react";

const OFFICE = ["super_admin", "admin", "office"];
const OFFICE_QS = ["super_admin", "admin", "office", "quantity_surveyor"];
const OFFICE_FIELD = ["super_admin", "admin", "office", "field"];

const GROUPS = [
  {
    label: "Work",
    items: [
      { label: "New Job", to: "/app/jobs/new", icon: Hammer, roles: OFFICE },
      { label: "Open Crew Planner", to: "/app/crew-planner?view=day", icon: Calendar, roles: OFFICE },
      { label: "New Crew Need", to: "/app/crew-needs?new=1", icon: ShoppingCart, roles: OFFICE },
    ],
  },
  {
    label: "Commercial",
    items: [
      { label: "New Takeoff", to: "/app/takeoffs?new=1", icon: PencilRuler, roles: OFFICE_QS },
      { label: "New Quote", to: "/app/quotes/new", icon: FileText, roles: OFFICE_QS },
      { label: "New Customer Invoice", to: "/app/customer-invoices/new", icon: Receipt, roles: OFFICE_QS },
      { label: "New Variation", to: "/app/variations?new=1", icon: FileSignature, roles: OFFICE_QS },
    ],
  },
  {
    label: "People",
    items: [
      { label: "New Timesheet", to: "/app/timesheets?new=1", icon: Clock, roles: OFFICE_FIELD },
      { label: "Leave Request", to: "/app/leave?new=1", icon: CalendarDays, roles: OFFICE_FIELD },
      { label: "Invite Team Mate", to: "/app/team?new=1", icon: UserPlus, roles: OFFICE },
    ],
  },
  {
    label: "Safety & QA",
    items: [
      { label: "Toolbox Talk", to: "/app/toolbox?new=1", icon: ClipboardCheck, roles: OFFICE_FIELD },
      { label: "Pre-Start", to: "/app/prestarts?new=1", icon: Wrench, roles: [...OFFICE_FIELD, "mechanic"] },
      { label: "Site Inspection", to: "/app/inspections?new=1", icon: ClipboardList, roles: OFFICE_FIELD },
      { label: "QA Record", to: "/app/qa?new=1", icon: ClipboardCheck, roles: OFFICE_FIELD },
      { label: "Log Hazard / Incident", to: "/app/safety?report=hazard", icon: ShieldAlert, roles: OFFICE_FIELD },
    ],
  },
];

export function QuickAdd({ role, onViewAll }) {
  const { trades } = useNavigationPresentation();
  const [open, setOpen] = useState(false);
  const nav = useNavigate();
  const groups = GROUPS
    .map((g) => ({ ...g, items: g.items.filter((a) => a.roles.includes(role) && (!trades || tradeActionVisible(a.to))).map(a => trades ? { ...a, label: tradeActionLabel(a) } : a) }))
    .filter((g) => g.items.length > 0);

  if (groups.length === 0) return null;

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          data-testid="quick-add-button"
          aria-label="Create"
          aria-haspopup="menu"
          className="h-9 pl-3 pr-2.5 rounded-lg inline-flex items-center gap-1.5 bg-[#FF5F15] text-white text-[12.5px] font-bold hover:brightness-110 active:scale-[0.98] transition-all shadow-[0_0_18px_rgba(255,95,21,0.3)]"
        >
          <Plus className="w-4 h-4" strokeWidth={2.8} />
          <span className="hidden sm:inline">Create</span>
          <ChevronDown className="w-3.5 h-3.5 opacity-80" />
        </button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={10}
        className="w-64 p-0 rounded-lg border-zinc-200 dark:border-[#1F2937] bg-white dark:bg-[#101620] text-zinc-900 dark:text-white overflow-hidden shadow-2xl"
        data-testid="quick-add-menu"
      >
        <div className="max-h-[70vh] overflow-y-auto py-1">
          {groups.map((g, gi) => (
            <div key={g.label} className={gi > 0 ? "mt-1 pt-1 border-t border-zinc-100 dark:border-white/[0.06]" : ""}>
              <div className="px-4 pt-2 pb-1 text-[10px] font-bold uppercase tracking-[0.16em] text-zinc-400 dark:text-zinc-500">
                {g.label}
              </div>
              {g.items.map((a) => {
                const Icon = a.icon;
                return (
                  <button
                    key={a.to}
                    type="button"
                    onClick={() => { setOpen(false); nav(a.to); }}
                    data-testid={`quick-add-${a.label.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                    className="w-full flex items-center gap-3 px-4 py-2 text-left text-[13px] font-semibold text-zinc-700 dark:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-[#151C27] hover:text-zinc-900 dark:hover:text-white transition-colors duration-150"
                  >
                    <Icon className="w-4 h-4 text-[#FF5F15] shrink-0" strokeWidth={2} />
                    {a.label}
                  </button>
                );
              })}
            </div>
          ))}
        </div>
        {onViewAll && (
          <button
            type="button"
            onClick={() => { setOpen(false); onViewAll(); }}
            data-testid="quick-add-view-all"
            className="w-full flex items-center justify-between px-4 py-2.5 border-t border-zinc-200 dark:border-[#1F2937] text-[11px] font-bold uppercase tracking-wider text-zinc-500 hover:text-[#FF5F15] transition-colors"
          >
            View all actions <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </PopoverContent>
    </Popover>
  );
}
